(() => {
  'use strict';
  if (window.MixWaveNavigation) return;

  const controllers = new Map();
  const all = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const visible = element => Boolean(element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  const focusable = scope => all('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', scope)
    .filter(element => visible(element) && !element.closest('[hidden], [inert]'));
  const pointInside = (point, rect) => point && point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
  const ease = 'cubic-bezier(.4,0,.6,1)';

  function mount(header) {
    if (controllers.has(header)) return;
    const abort = new AbortController();
    const on = (target, type, fn, options = {}) => target?.addEventListener(type, fn, { ...options, signal: abort.signal });
    const hover = matchMedia('(hover: hover) and (pointer: fine)');
    const breakpoint = matchMedia('(min-width: 1024px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const disclosures = all('[data-mw-desktop-menu], [data-mw-mobile-menu]', header);
    const states = disclosures.map(menu => ({
      menu,
      trigger: menu.querySelector(':scope > summary'),
      panel: menu.querySelector('[data-mw-nav-panel], [data-mw-mobile-panel]'),
      mobile: menu.hasAttribute('data-mw-mobile-menu'),
      wanted: false,
      phase: 'closed',
      animation: null,
      generation: 0,
      intentTimer: 0,
      intentKind: '',
      keyboard: false,
      hoverClosing: false
    })).filter(state => state.trigger && state.panel);
    const mobile = states.find(state => state.mobile);
    const desktop = states.filter(state => !state.mobile);
    const views = mobile ? all('[data-mw-mobile-view]', mobile.panel) : [];
    const viewById = new Map(views.map(view => [view.id, view]));
    const rootView = views.find(view => !view.dataset.mwMobileParent) || views[0];
    const backButton = mobile?.panel.querySelector('[data-mw-mobile-back]');
    const origins = new Map();
    const inertRecords = new Map();
    let activeView = rootView;
    let viewAnimation = null;
    let inputMode = 'pointer';
    let pointer = null;
    let frame = 0;

    function position() {
      header.style.setProperty('--mw-navigation-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    }
    function resetColumnFit(state) {
      all('[data-mw-custom-columns] [data-mw-nav-entry]', state.panel).forEach(row => { row.hidden = false; });
      state.panel.style.removeProperty('--mw-navigation-available-height');
      state.panel.classList.remove('mw-mega-menu--compact');
    }
    function fitColumns(state) {
      const columns = state.panel.querySelector('[data-mw-custom-columns]');
      if (!columns) return;
      const focused = document.activeElement;
      resetColumnFit(state);
      const available = Math.max(0, window.innerHeight - Math.max(0, header.getBoundingClientRect().bottom) - 8);
      state.panel.style.setProperty('--mw-navigation-available-height', `${available}px`);
      state.panel.classList.toggle('mw-mega-menu--compact', available < 460);
      const groups = all('[data-mw-column-source="products"]', columns).map(group => ({
        group, rows: all('[data-mw-nav-entry]:not([data-mw-manual-link])', group)
      }));
      const columnSpace = Math.max(0, available - (state.panel.scrollHeight - columns.getBoundingClientRect().height));
      while (state.panel.scrollHeight > available + 1) {
        const candidates = groups.filter(group => group.rows.filter(row => !row.hidden).length > 1 && group.group.getBoundingClientRect().height > columnSpace + 1);
        if (!candidates.length) break;
        candidates.sort((a, b) => b.group.getBoundingClientRect().height - a.group.getBoundingClientRect().height);
        candidates[0].rows.filter(row => !row.hidden).at(-1).hidden = true;
      }
      if (columns.contains(focused) && focused.closest('[hidden]')) {
        (focused.closest('[data-mw-nav-column]')?.querySelector('.mw-mega-menu__category a') || state.trigger).focus({ preventScroll: true });
      }
    }
    function sync() {
      states.forEach(state => state.trigger.setAttribute('aria-expanded', String(state.wanted)));
      header.classList.toggle('mw-navigation-is-open', states.some(state => state.menu.open));
      document.documentElement.classList.toggle('mw-mobile-navigation-open', Boolean(mobile?.menu.open));
    }
    function phase(state, value) {
      state.phase = value;
      state.menu.dataset.mwState = value;
      state.panel.dataset.mwState = value;
    }
    function cancelIntent(state) {
      clearTimeout(state.intentTimer);
      state.intentTimer = 0;
      state.intentKind = '';
    }
    function intent(state, kind, callback, delay) {
      if (state.intentKind === kind) return;
      cancelIntent(state);
      state.intentKind = kind;
      state.intentTimer = setTimeout(() => {
        state.intentTimer = 0;
        state.intentKind = '';
        callback();
      }, delay);
    }
    function cancelAnimation(state) {
      state.generation += 1;
      state.animation?.cancel();
      state.animation = null;
    }
    function clearPanelStyles(state) {
      state.panel.style.removeProperty('height');
      state.panel.style.removeProperty('overflow');
      state.panel.style.removeProperty('clip-path');
    }
    function setBackgroundInert() {
      if (!mobile) return;
      if (inertRecords.size) {
        inertRecords.forEach((value, element) => { element.inert = true; });
        return;
      }
      let branch = mobile.panel;
      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;
        [...parent.children].forEach(sibling => {
          if (sibling === branch || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) return;
          inertRecords.set(sibling, sibling.getAttribute('inert'));
          sibling.inert = true;
        });
        branch = parent;
      }
    }
    function restoreBackground() {
      inertRecords.forEach((value, element) => {
        if (value === null) element.removeAttribute('inert');
        else element.setAttribute('inert', value);
      });
      inertRecords.clear();
    }
    function updateViewControls() {
      if (!mobile || !activeView) return;
      const parent = viewById.get(activeView.dataset.mwMobileParent);
      if (backButton) {
        backButton.hidden = !parent;
        backButton.setAttribute('aria-label', parent ? `Back to ${parent.dataset.mwMobileTitle || 'navigation'}` : 'Back');
      }
      mobile.panel.setAttribute('aria-label', activeView.dataset.mwMobileTitle || 'Navigation');
    }
    function showView(next, { back = false, origin = null, focus = true, animate = true } = {}) {
      if (!mobile || !next) return;
      const previous = activeView;
      const returningTo = back && previous ? origins.get(previous.id) : null;
      viewAnimation?.cancel();
      viewAnimation = null;
      if (origin) origins.set(next.id, origin);
      activeView = next;
      views.forEach(view => {
        view.hidden = view !== next;
        view.inert = view !== next;
      });
      next.dataset.mwDirection = back ? 'back' : 'forward';
      mobile.panel.scrollTop = 0;
      next.scrollTop = 0;
      updateViewControls();
      if (animate && previous !== next && !reduced.matches && next.animate) {
        viewAnimation = next.animate([
          { opacity: 0, transform: `translateX(${back ? -12 : 12}px)` },
          { opacity: 1, transform: 'translateX(0)' }
        ], { duration: 280, easing: 'cubic-bezier(.2,.7,.2,1)' });
      }
      if (focus) {
        const target = returningTo && next.contains(returningTo) ? returningTo : focusable(next)[0];
        (target || mobile.panel).focus({ preventScroll: true });
      }
    }
    function resetViews() {
      showView(rootView, { focus: false, animate: false });
      origins.clear();
    }
    function goBack() {
      const parent = viewById.get(activeView?.dataset.mwMobileParent);
      if (!parent) return false;
      showView(parent, { back: true });
      return true;
    }
    function finish(state, opening) {
      cancelAnimation(state);
      if (opening) {
        phase(state, 'open');
        clearPanelStyles(state);
        if (!state.mobile) fitColumns(state);
      } else {
        state.menu.open = false;
        phase(state, 'closed');
        clearPanelStyles(state);
        state.panel.inert = false;
        if (state.mobile) {
          restoreBackground();
          state.panel.removeAttribute('role');
          state.panel.removeAttribute('aria-modal');
          resetViews();
          state.panel.removeAttribute('aria-label');
        }
      }
      sync();
    }
    function animatePanel(state, opening, from) {
      if (reduced.matches || !state.panel.animate) {
        finish(state, opening);
        return;
      }
      let frames;
      let options;
      if (state.mobile) {
        frames = [
          { clipPath: from || (opening ? 'inset(0 0 100% 0)' : 'inset(0 0 0% 0)') },
          { clipPath: opening ? 'inset(0 0 0% 0)' : 'inset(0 0 100% 0)' }
        ];
        options = { duration: 280, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both' };
      } else {
        state.panel.style.removeProperty('height');
        const target = opening ? state.panel.getBoundingClientRect().height : 0;
        state.panel.style.height = `${from}px`;
        state.panel.style.overflow = 'hidden';
        frames = [{ height: `${from}px` }, { height: `${target}px` }];
        options = { duration: 240, delay: opening ? 0 : 120, easing: ease, fill: 'both' };
      }
      const generation = state.generation;
      const animation = state.panel.animate(frames, options);
      state.animation = animation;
      animation.finished.then(() => {
        if (state.generation === generation && state.wanted === opening) finish(state, opening);
      }).catch(() => {});
    }
    function open(state, { keyboard = inputMode === 'keyboard', focus = false } = {}) {
      cancelIntent(state);
      if (!visible(state.trigger) || state.wanted) return;
      states.forEach(other => { if (other !== state) close(other, { immediate: true }); });
      const wasOpen = state.menu.open;
      const from = state.mobile
        ? (wasOpen ? getComputedStyle(state.panel).clipPath : 'inset(0 0 100% 0)')
        : (wasOpen ? state.panel.getBoundingClientRect().height : 0);
      cancelAnimation(state);
      state.wanted = true;
      state.hoverClosing = false;
      state.keyboard = keyboard;
      state.panel.inert = false;
      if (!wasOpen) phase(state, 'closed');
      state.menu.open = true;
      position();
      if (!state.mobile) fitColumns(state);
      if (state.mobile) {
        state.panel.setAttribute('role', 'dialog');
        state.panel.setAttribute('aria-modal', 'true');
        if (!wasOpen) resetViews();
        updateViewControls();
        setBackgroundInert();
      }
      if (!wasOpen && !state.mobile) {
        state.panel.style.height = '0px';
        state.panel.style.overflow = 'hidden';
        state.panel.getBoundingClientRect();
      }
      phase(state, 'opening');
      sync();
      animatePanel(state, true, from === 'none' ? undefined : from);
      if (state.mobile) (focusable(activeView || state.panel)[0] || state.panel).focus({ preventScroll: true });
      else if (focus) focusable(state.panel)[0]?.focus({ preventScroll: true });
    }
    function close(state, { restoreFocus = false, immediate = false, hoverClose = false } = {}) {
      cancelIntent(state);
      if (!state.menu.open) return;
      if (!state.wanted && !immediate) return;
      const from = state.mobile ? getComputedStyle(state.panel).clipPath : state.panel.getBoundingClientRect().height;
      cancelAnimation(state);
      state.wanted = false;
      state.hoverClosing = hoverClose;
      state.keyboard = false;
      if ((restoreFocus || state.panel.contains(document.activeElement)) && visible(state.trigger)) {
        if (state.mobile) state.trigger.inert = false;
        state.trigger.focus({ preventScroll: true });
      }
      state.panel.inert = true;
      phase(state, 'closing');
      sync();
      if (immediate) finish(state, false);
      else animatePanel(state, false, from === 'none' ? undefined : from);
    }
    function closeAll(options = {}) { states.forEach(state => close(state, options)); }
    function keyboardPinned(state) {
      return state.keyboard && state.menu.contains(document.activeElement);
    }
    function inHoverRegion(state) {
      return pointInside(pointer, header.getBoundingClientRect()) || pointInside(pointer, state.panel.getBoundingClientRect());
    }
    function checkPointer(event) {
      if (event.pointerType !== 'mouse' || !hover.matches || !breakpoint.matches) return;
      pointer = { x: event.clientX, y: event.clientY };
      desktop.forEach(state => {
        if (!state.menu.open || keyboardPinned(state)) return;
        const otherLink = event.target.closest?.('.mw-navigation__link');
        if (otherLink && otherLink !== state.trigger && header.contains(otherLink)) {
          close(state);
        } else if (inHoverRegion(state)) {
          if (state.intentKind === 'leave') cancelIntent(state);
          if (!state.wanted && state.hoverClosing) open(state, { keyboard: false });
        } else if (state.wanted) {
          intent(state, 'leave', () => {
            if (!keyboardPinned(state) && !inHoverRegion(state)) close(state, { hoverClose: true });
          }, 150);
        }
      });
    }

    states.forEach(state => {
      state.menu.open = false;
      phase(state, 'closed');
      on(state.trigger, 'click', event => {
        event.preventDefault();
        if (state.wanted) close(state, { restoreFocus: true });
        else open(state);
      });
      on(state.trigger, 'keydown', event => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        open(state, { keyboard: true });
        const links = focusable(state.mobile ? activeView || state.panel : state.panel);
        (event.key === 'ArrowUp' ? links.at(-1) : links[0])?.focus({ preventScroll: true });
      });
      on(state.menu, 'focusin', () => {
        if (inputMode === 'keyboard') state.keyboard = true;
      });
      on(state.menu, 'focusout', event => {
        if (state.mobile || state.menu.contains(event.relatedTarget)) return;
        intent(state, 'focusout', () => {
          if (!state.menu.contains(document.activeElement)) close(state);
        }, 0);
      });
      on(state.menu, 'click', event => {
        const target = event.target.closest('a, button');
        if (!target) return;
        if (target.matches('[data-mw-nav-close]')) {
          event.preventDefault();
          close(state, { restoreFocus: true });
        } else if (state.mobile && target.matches('[data-mw-mobile-back]')) {
          event.preventDefault();
          goBack();
        } else if (state.mobile && target.matches('[data-mw-mobile-go]') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          const next = viewById.get(target.dataset.mwMobileGo);
          if (next) {
            event.preventDefault();
            showView(next, { origin: target });
          }
        } else if (target.matches('a[href]')) close(state, { immediate: true });
      });
      on(state.menu, 'toggle', () => {
        if (!state.menu.open && state.phase !== 'closed') {
          state.wanted = false;
          finish(state, false);
        } else if (state.menu.open && state.phase === 'closed') open(state);
      });
    });
    desktop.forEach(state => {
      on(state.trigger, 'pointerenter', event => {
        if (event.pointerType !== 'mouse' || !hover.matches || !breakpoint.matches || header.dataset.hoverMenu !== 'true') return;
        if (state.wanted) cancelIntent(state);
        else intent(state, 'enter', () => open(state, { keyboard: false }), 100);
      });
      on(state.trigger, 'pointerleave', () => {
        if (state.intentKind === 'enter') cancelIntent(state);
      });
    });
    on(document, 'pointerdown', event => {
      inputMode = 'pointer';
      states.forEach(state => {
        if (state.menu.contains(event.target)) state.keyboard = false;
        else close(state);
      });
    }, { capture: true });
    on(document, 'pointerout', event => {
      if (event.relatedTarget !== null || event.pointerType !== 'mouse' || !hover.matches || !breakpoint.matches) return;
      pointer = null;
      desktop.forEach(state => {
        cancelIntent(state);
        if (!keyboardPinned(state)) close(state, { hoverClose: true });
      });
    });
    on(document, 'wheel', event => {
      if (!breakpoint.matches || event.ctrlKey || (!event.deltaX && !event.deltaY)) return;
      desktop.forEach(state => {
        state.hoverClosing = false;
        close(state);
      });
    }, { capture: true, passive: true });
    on(document, 'keydown', event => {
      if (!['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) inputMode = 'keyboard';
      states.forEach(state => {
        if (state.menu.contains(event.target)) state.keyboard = true;
      });
      if (event.defaultPrevented) return;
      if (mobile?.wanted && event.key === 'Tab') {
        const links = focusable(mobile.panel);
        const first = links[0];
        const last = links.at(-1);
        if (!first) { event.preventDefault(); mobile.panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !mobile.panel.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !mobile.panel.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
      if (event.key !== 'Escape') return;
      const state = states.find(item => item.wanted);
      if (!state) return;
      event.preventDefault();
      event.stopPropagation();
      if (state.mobile && goBack()) return;
      close(state, { restoreFocus: true });
    }, { capture: true });
    on(document, 'click', event => {
      if (event.target.closest('[data-mw-dialog-open], .js-search-header, .js-drawer-open-cart, [data-cart-open]')) closeAll({ immediate: true });
    }, { capture: true });
    on(document, 'predictive-search:open', event => {
      if (event.detail?.context === 'header') closeAll({ immediate: true });
    }, { capture: true });
    on(document, 'modalOpen', () => closeAll({ immediate: true }), { capture: true });
    on(document, 'cart:open', () => closeAll({ immediate: true }), { capture: true });
    on(document, 'drawerOpen', event => {
      const hadMenu = states.some(state => state.menu.open);
      closeAll({ immediate: true });
      const drawer = document.getElementById(event.detail?.id || '') || document.querySelector('.drawer--is-open');
      if (hadMenu && visible(drawer) && !drawer.contains(document.activeElement)) {
        drawer.focus({ preventScroll: true });
      }
    });
    const update = () => {
      if (frame || !states.some(state => state.menu.open)) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        position();
        desktop.filter(state => state.phase === 'open').forEach(fitColumns);
      });
    };
    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', update, { passive: true });
    on(document.fonts, 'loadingdone', update);
    const resetForMode = () => {
      const focused = states.some(state => state.menu.contains(document.activeElement));
      closeAll({ immediate: true });
      if (focused) (states.find(state => visible(state.trigger))?.trigger || header.querySelector('a[href]'))?.focus({ preventScroll: true });
    };
    on(breakpoint, 'change', resetForMode);
    on(hover, 'change', resetForMode);
    on(reduced, 'change', () => {
      if (reduced.matches) states.filter(state => state.animation).forEach(state => finish(state, state.wanted));
      viewAnimation?.cancel();
    });
    const observer = new ResizeObserver(update);
    observer.observe(header);
    resetViews();
    sync();
    controllers.set(header, {
      checkPointer,
      cleanup() {
        closeAll({ immediate: true });
        restoreBackground();
        abort.abort();
        observer.disconnect();
        cancelAnimationFrame(frame);
        viewAnimation?.cancel();
        states.forEach(state => { cancelIntent(state); cancelAnimation(state); clearPanelStyles(state); resetColumnFit(state); });
        controllers.delete(header);
      }
    });
  }

  function init(scope = document) {
    if (scope.matches?.('[data-mw-navigation]')) mount(scope);
    all('[data-mw-navigation]', scope).forEach(mount);
  }
  function destroy(scope) {
    controllers.forEach((controller, header) => { if (scope === header || scope.contains(header)) controller.cleanup(); });
  }
  document.addEventListener('pointermove', event => controllers.forEach(controller => controller.checkPointer(event)), { passive: true });
  window.MixWaveNavigation = { init, destroy };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
